import { setError } from '../redux/actions/alertActions'
import config from '../config.json'
import axios from 'axios';
import store from '../redux/store'; // убедитесь, что путь правильный

// Глобальный флаг, чтобы предотвратить повторные запросы во время выхода
let isLoggingOut = false;

export default class Config {
  constructor() {
    this.dispatch = store.dispatch;

    let fullUrl = ''

    if (process.env.NODE_ENV === 'development') {
      fullUrl = config.serverUrl;

      if (config.serverPort) {
        fullUrl = fullUrl + ':' + config.serverPort;
      }
    } else {
      fullUrl = fullUrl + '/api';
    }

    this.fullUrl = fullUrl

    // Интерсептор запросов
    axios.interceptors.request.use(
      config => {
        const jwt = this.getStoredJwt();
        if (jwt?.jwt) {
          config.headers['DUJWT'] = jwt?.jwt;
        }
        return config;
      },
      error => Promise.reject(error)
    );

    // Интерсептор ответов
    axios.interceptors.response.use(
      res => res,
      async (error) => {
        if (isLoggingOut) {
          return Promise.reject(error);
        }

        const originalReq = error.config;

        if (!error.response) {
          console.error('Network error:', error);
          this.dispatch(setError());
          return Promise.reject(error);
        }

        if (error.response.status === 401) {
          if (originalReq.url.includes('refresh')) {
            // Если refresh сам вернул 401, сразу выходим
            await this.performLogout();
            console.error('Refresh token error', error);
            return Promise.reject(error);
          } else if (!originalReq._retry) {
            originalReq._retry = true;

            const jwt = this.getStoredJwt();
            const refreshToken = jwt?.refresh_token;

            // Проверяем, есть ли refresh-токен перед попыткой обновления
            if (!refreshToken) {
              await this.performLogout();
              return Promise.reject(error);
            }

            try {
              const refreshSuccess = await this.#renew(refreshToken);
              if (refreshSuccess) {
                return axios(originalReq);
              } else {
                await this.performLogout();
                return Promise.reject(error);
              }
            } catch (refreshErr) {
              console.error('Error during token refresh:', refreshErr);
              await this.performLogout();
              return Promise.reject(error);
            }
          }
        }

        console.error('Server responded with error:', error);
        return Promise.reject(error);
      }
    );
  }

  /**
   * Безопасно получает JWT из localStorage
   */
  getStoredJwt() {
    try {
      return JSON.parse(localStorage.getItem('jwt')) || null;
    } catch {
      return null;
    }
  }

  /**
   * Выполняет logout с защитой от повторных вызовов
   */
  async performLogout() {
    if (isLoggingOut) return;

    isLoggingOut = true;

    try {
      const jwt = this.getStoredJwt();
      if (jwt?.refresh_token) {
        // Отправляем запрос logout только если есть refresh-токен
        try {
          await this.request('logout', 'POST', {
            refresh_token: jwt.refresh_token
          });
        } catch (logoutErr) {
          console.error('Logout request failed:', logoutErr);
          // Продолжаем выполнение, даже если logout на сервере не удался
        }
      }
    } catch (err) {
      console.error('Error during logout process:', err);
    } finally {
      // В любом случае очищаем локальное хранилище и выполняем логику выхода
      localStorage.removeItem('jwt');
      localStorage.removeItem('user');
      // logOut(); // используем функцию из хуков
      window.location.href = '/login'; // используем редирект как в первоначальном коде
      isLoggingOut = false;
    }
  }

  request = async (url, method = 'POST', body, skipParseResponse = false, failSilently = false) => {
    if (isLoggingOut) {
      return null;
    }

    let fullUrl = this.fullUrl + '/' + url;

    if (method === 'GET' && body) {
      let queryParams = '';

      if (body instanceof URLSearchParams) {
        queryParams = body.toString();
      } else if (typeof body === 'object' && body !== null) {
        queryParams = new URLSearchParams(body).toString();
      }

      if (queryParams) {
        const separator = fullUrl.includes('?') ? '&' : '?';
        fullUrl = fullUrl + separator + queryParams;
      }
    }


    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    let requestBody = null;
    if (method !== 'GET' && body) {
      requestBody = body;
    }

    try {
      const res = await axios.request({
        url: fullUrl,
        method: method,
        headers: headers,
        data: requestBody,
      });

      // Проверяем статус ответа
      if (res.status >= 200 && res.status <= 299) {
        if (res && !skipParseResponse) {
          return res.data;
        }
        return res; // возвращаем объект ответа, если skipParseResponse = true
      } else {
        // Обработка ошибок с сервера
        !failSilently && this.dispatch(
          setError(`Server Error: ${res.status}${res.statusText ? ', ' + res.statusText : ''}`)
        );
        return null;
      }
    } catch (error) {
      // Обработка ошибок axios
      if (error.response) {
        // Сервер ответил с ошибкой
        !failSilently && this.dispatch(
          setError(`Server Error: ${error.response.status} ${error.response.statusText}`)
        );
      } else if (error.request) {
        // Запрос был сделан, но не получен ответ
        !failSilently && this.dispatch(setError('Network Error'));
      } else {
        // Ошибка при настройке запроса
        !failSilently && this.dispatch(setError('Request Setup Error'));
      }
      return null;
    }
  };

  logout = async () => {
    // Просто вызываем performLogout, который уже содержит всю логику
    await this.performLogout();
  };

  #renew = async (refreshToken) => {
    try {
      const res = await this.request('refresh', 'POST', {
        refresh_token: refreshToken
      });

      if (res && res.jwt) {
        // Успешное обновление токена
        localStorage.setItem('jwt', JSON.stringify(res));
        return true;
      } else {
        // Сервер не вернул новый токен
        console.error('Token refresh failed: no new token received');
        return false;
      }
    } catch (error) {
      console.error('Error during token refresh:', error);
      return false;
    }
  };
}